import React, { useEffect, useRef, useState } from 'react'
import animateScrollTo from 'animated-scroll-to'
import { Grid, GridCell, View, useWindowResizeSensor } from 'oio-react'
import PropTypes from 'prop-types'
import { withSize } from 'react-sizeme'
import ArrowLeftIcon from 'assets/icons/arrowLeft'
import ArrowRightIcon from 'assets/icons/arrowRight'
import { compose } from 'src/components/utils'
import style from './style'

// =======================================================
// Component
// =======================================================

const Carousel = ({
   arrowOffsetFromTop,
   children,
   env,
   onRotate,
   numSlidesPerPane,
   size,
   spacing
}) => {
   const container = useRef(null)
   const [currentPaneIndex, setCurrentPaneIndex] = useState(0)
   const [componentWidth, setComponentWidth] = useState(size.width)

   const { currentBreakpoint } = useWindowResizeSensor()
   const numItems = children.length

   // The Carousel component distributes all slide into 'rotatable' panes
   // Only one pane is visible at a time
   const numPanes = Math.ceil(numItems / numSlidesPerPane[currentBreakpoint])
   const paneContainerWidth = `calc(${numPanes}00% + ${(numPanes - 1) * spacing}px)`
   const gridColumns = numPanes * numSlidesPerPane[currentBreakpoint]

   // Prev / Next Arrow Buttons
   const nextSlideIndex = currentPaneIndex + 1
   const prevSlideIndex = currentPaneIndex - 1
   const showPrevArrowButton = currentPaneIndex > 0
   const showNextArrowButton = currentPaneIndex + 1 < numPanes

   // We use react-sizeme to detemine if the Carousel component
   // has been resized (size.width prop). The listener has been debounced to every 100ms
   // to prevent over-rendering on window resize
   useEffect(() => {
      setComponentWidth(size.width)
      // When window is resized, the currentPaneIndex and total number of panes
      // might change. For example, at viewport size A,
      // you might be on currentPaneIndex 9 of 10.
      // But at size D, you may only have a total of only 6 panes.
      // The currentPaneIndex needs to be set to accomodate for this
      const currentSlideNumber = currentPaneIndex + 1
      if (numPanes > 0 && currentSlideNumber > numPanes) {
         setCurrentPaneIndex(numPanes - 1)
      }
   }, [numPanes, size.width])

   // If currentPaneIndex is updated or the componentWidth is updated (resized),
   // we scroll to new position
   useEffect(() => {
      const offset = currentPaneIndex * spacing
      const slideToPosition = (container.current.offsetWidth * currentPaneIndex) + offset

      animateScrollTo(slideToPosition, {
         element: container.current,
         horizontal: true,
         cancelOnUserAction: false,
         speed: 500
      })

      if (onRotate) {
         onRotate(currentPaneIndex + 1, numPanes)
      }
   }, [currentPaneIndex, componentWidth, numPanes])

   return (
      <View width="100%">
         <View
            ref={container}
            width="100%"
            style={{ overflow: 'hidden' }}>
            <View width={paneContainerWidth}>
               <Grid
                  columns={gridColumns}
                  spacing={`${spacing}px`}>
                  {React.Children.map(children, child => (
                     <GridCell>
                        {child}
                     </GridCell>
                  ))}
               </Grid>
            </View>
         </View>
         {showPrevArrowButton && (
            <View
               id="carouselLeftArrowButton"
               role="button"
               onKeyUp={() => setCurrentPaneIndex(prevSlideIndex)}
               onClick={() => setCurrentPaneIndex(prevSlideIndex)}
               tabIndex={0}
               top={`
                  calc(${arrowOffsetFromTop} - 18px - ${spacing / 2}px)[a]
                  calc(${arrowOffsetFromTop} - 24px - ${spacing / 2}px)[b-f]
               `}
               left="-12px[a] -24px[b-f]"
               {...style.arrowButton}>
               <ArrowLeftIcon
                  width="12px"
                  height="12px"
                  strokeWidth="4px"
                  color="#555"
               />
            </View>
         )}
         {showNextArrowButton && (
            <View
               id="carouselRightArrowButton"
               role="button"
               onKeyUp={() => setCurrentPaneIndex(nextSlideIndex)}
               onClick={() => setCurrentPaneIndex(nextSlideIndex)}
               tabIndex={0}
               top={`
                  calc(${arrowOffsetFromTop} - 18px - ${spacing / 2}px)[a]
                  calc(${arrowOffsetFromTop} - 24px - ${spacing / 2}px)[b-f]
               `}
               right="-12px[a] -24px[b-f]"
               {...style.arrowButton}>
               <ArrowRightIcon
                  width="12px"
                  height="12px"
                  strokeWidth="4px"
                  color="#555"
               />
            </View>
         )}
      </View>
   )
}

// =======================================================
// Props & Export
// =======================================================

Carousel.propTypes = {
   arrowOffsetFromTop: PropTypes.string,
   children: PropTypes.node.isRequired,
   onRotate: PropTypes.func,
   spacing: PropTypes.number,
   // TODO: This will likely be restructured later to use OIOResponsiveObject
   numSlidesPerPane: PropTypes.shape({
      a: PropTypes.number.isRequired,
      b: PropTypes.number.isRequired,
      c: PropTypes.number.isRequired,
      d: PropTypes.number.isRequired,
      e: PropTypes.number.isRequired,
      f: PropTypes.number.isRequired
   }).isRequired
}

Carousel.defaultProps = {
   arrowOffsetFromTop: '50%',
   onRotate: undefined,
   spacing: 12
}

export default compose(
   withSize({
      refreshRate: 100,
      refreshMode: 'debounce'
   })
)(Carousel)
